import React, { useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Components
import ErrorHandler from '../../../_molecules/ErrorHandler';
import MembershipIsThisYou from './MembershipIsThisYou';
import MembershipNameMatches from './MembershipNameMatches';
import MembershipNoMatches from './MembershipNoMatches';
import MembershipSearchByEmailForm from './MembershipSearchByEmailForm';
import MembershipSearchStatusText from './MembershipSearchStatusText';
import MemberSelection from '@molecules/MemberSelection';
import SearchMembersByName from '@molecules/SearchMembersByName';
import { H3, SectionWrapperInner, SectionWrapperOuter } from '@styled';
import TrackPageView from '@tracking/TrackPageView';

// Functionality
import { LoadingContext } from '@context/LoadingProvider';
import { getMemberFromCache } from '@general/cache/memberCache';
import { addMembershipToCart, membershipYears } from '@general/helpers/cart.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useMembersIneligibleForMembership from '@hooks/useMembersIneligibleForMembership';
import useViewer from '@hooks/useViewer';
import { MembershipContext } from '../../../../context/MembershipProvider';
import PasswordChangeContextProvider from '@context/PasswordChangeProvider';

const MembershipStartPage = () => {
    const {
        isEmailValid,
        joinStep,
        joinSteps,
        matchResultsByCompanyAndEmail,
        matchResultsByName,
        matchResultsRejected,
        searchStatus,
        setMatchResultsByName,
        setMembershipFor,
    } = useContext(MembershipContext);

    const { setIsLoading } = useContext(LoadingContext);
    const history = useHistory();
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { membersIneligibleForMembership } = useMembersIneligibleForMembership();
    const { search, pathname: returnUrl } = useLocation();

    const redirectAfterMemberSelection = async ({ whoFor, selectedCoworkers }) => {
        setMembershipFor(selectedCoworkers);

        if (whoFor === 'me') {
            setIsLoading(true);
            const me = await getMemberFromCache(viewer.recordId);

            if (me.ofac_error) {
                history.push('/membership/hold');
            } else {
                history.push('/membership/info');
            }

            setIsLoading(false);
        } else if (whoFor === 'coworker') {
            setIsLoading(true);

            // Have to add sequentially instead of using Promise.all() because it's
            // the only way to reliably end up with the correct cart state.
            await selectedCoworkers
                .map(coworker => () => {
                    return addMembershipToCart({
                        cart,
                        memberId: coworker.value,
                        viewerId: viewer.recordId,
                        years: membershipYears.THREE,
                    });
                })
                .reduce(async (promise, next) => {
                    await promise;
                    return next();
                }, Promise.resolve());

            history.push(`/cart/view`);
            setIsLoading(false);
        } else if (whoFor === 'new') {
            history.push(`/membership/info/new`);
        }
    };

    const redirectOnSuccessfulSearch = email => {
        const query = new URLSearchParams(search);
        query.append('returnUrl', returnUrl);
        history.push({
            pathname: `/login`,
            search: `${query}`,
            state: { email },
        });

        return null;
    };

    if (cart && !membersIneligibleForMembership) {
        return null;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <div className="container">
                            <div className="gap-8 grid-cols-8 md:grid">
                                <div className="col-span-5">
                                    {!matchResultsByName && (
                                        <>
                                            {joinStep !== joinSteps.WHO_FOR && (
                                                <MembershipSearchByEmailForm
                                                    onSuccessfulSearch={redirectOnSuccessfulSearch}
                                                />
                                            )}
                                            {searchStatus && isEmailValid && <MembershipSearchStatusText />}

                                            {joinStep === joinSteps.SEARCH_NAME && isEmailValid && (
                                                <SearchMembersByName onSearchCompleted={setMatchResultsByName} />
                                            )}

                                            {joinStep === joinSteps.WHO_FOR && (
                                                <>
                                                    <H3 className="mb-1.125rem">Purchase a Membership</H3>
                                                    <MemberSelection
                                                        buttonText="Continue to Membership"
                                                        headingText="Who is this membership for?"
                                                        membersToExclude={membersIneligibleForMembership || []}
                                                        onSubmit={redirectAfterMemberSelection}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}

                                    {matchResultsByName &&
                                        matchResultsByName.count > 0 &&
                                        !matchResultsByCompanyAndEmail &&
                                        !matchResultsRejected && <MembershipNameMatches />}

                                    {((matchResultsByName && matchResultsByName.count === 0) ||
                                        (matchResultsByCompanyAndEmail && matchResultsByCompanyAndEmail.count !== 1) ||
                                        matchResultsRejected) && (
                                        <PasswordChangeContextProvider>
                                            <MembershipNoMatches />
                                        </PasswordChangeContextProvider>
                                    )}

                                    {matchResultsByCompanyAndEmail &&
                                        matchResultsByCompanyAndEmail.count === 1 &&
                                        !matchResultsRejected && <MembershipIsThisYou />}
                                </div>
                            </div>
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default MembershipStartPage;
